import { type ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { isSortable } from '@dnd-kit/dom/sortable';
import { DragDropProvider } from '@dnd-kit/react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import Alert from 'components/shared/Alert';
import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import CloseButton from 'components/shared/CloseButton';
import Foldout from 'components/shared/Foldout';
import Checkbox from 'components/shared/form/Checkbox';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import { DISPLAY_TEXT, OPTION_LIST, WorkflowLookup, WorkflowLookupItem } from 'models/WorkflowLookup';
import {
  adminWorkflowLookupsQueryKeys,
  indexAdminWorkflowLookups,
  showAdminWorkflowLookup,
  updateAdminWorkflowLookup,
} from 'services/api/admin/workflowLookups';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import PencilIconOutline from 'svg/PencilIconOutline';

import {
  blankItem,
  isCustomized,
  itemErrors,
  parentKeysFor,
  sentence,
  slugify,
  toInput,
  withKeys,
} from './lookupItemHelpers';
import LookupItemRow from './LookupItemRow';

type DragEndHandler = NonNullable<ComponentProps<typeof DragDropProvider>['onDragEnd']>;

class StaleListError extends Error {
  constructor() {
    super('Someone else changed this list while it was open. Reload to see their changes, then edit again.');
  }
}

function saveErrorText(error: unknown) {
  if (error instanceof StaleListError) return error.message;

  const response = (error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
    ?.response?.data;
  const validation = response?.errors && Object.values(response.errors).flat()[0];

  return response?.message ?? validation ?? 'That list could not be saved.';
}

type Props = {
  lookup: WorkflowLookup;
  onClose: () => void;
};

// The entries of one list, editable in place. What is allowed depends on which family the list
// belongs to, and the API says which — the screen never decides that per key.
export default function WorkflowLookupSlideout({ lookup, onClose }: Props) {
  const { profile } = useProfile();
  const { addToast } = useToastActions();
  const queryClient = useQueryClient();

  const [items, setItems] = useState<WorkflowLookupItem[] | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [alphabetized, setAlphabetized] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);

  // Mounted closed, then opened on the next frame so the panel slides in rather than appearing.
  // Same as the sibling admin panels.
  useEffect(() => {
    requestAnimationFrame(() => setOpen(true));
  }, []);

  const animateClose = useCallback(() => {
    setOpen(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  const canEdit = profile.has(PERMISSIONS.adminWorkflowLookupEdit) && lookup.editable;

  const { data: loaded, isLoading } = useQuery({
    queryKey: adminWorkflowLookupsQueryKeys.show(lookup.id),
    queryFn: ({ signal }) => showAdminWorkflowLookup(lookup.id, signal),
  });

  // A lookup field offers the parent list's entries FOR THE SAME SCOPE. A client editing its own
  // denial reasons must choose from that client's own types, not the Olio defaults, or the screen
  // would offer a parent the save is then refused for.
  const parentKeys = useMemo(() => parentKeysFor(lookup), [lookup]);
  const parentQueries = useQueries({
    queries: parentKeys.map((key) => ({
      queryKey: adminWorkflowLookupsQueryKeys.index({ key, clientId: lookup.clientId ?? 'global' }),
      queryFn: async ({ signal }: { signal?: AbortSignal }) => {
        // A page big enough to hold the global row plus every client's copy of this key. The
        // default page is 25, and the global row is not guaranteed to be on it once enough clients
        // have customized — which would render every picker empty on the Olio default list itself.
        const page = await indexAdminWorkflowLookups(
          { key, clientId: lookup.clientId ?? undefined, pageSize: 200 },
          signal
        );
        // Client-over-global, the same rule the engine resolves by: the client's own row wins when
        // it has one, otherwise the global row is what that client actually gets.
        const row =
          page.data.find((candidate) => candidate.clientId === lookup.clientId) ??
          page.data.find((candidate) => candidate.global);

        return row ? showAdminWorkflowLookup(row.id, signal) : null;
      },
    })),
  });

  const parentOptions = useMemo(() => {
    const byKey: Record<string, WorkflowLookupItem[]> = {};
    parentKeys.forEach((key, index) => {
      const row = parentQueries[index]?.data;
      byKey[key] = (row?.items ?? []).filter((item) => !item.archived);
    });

    return byKey;
  }, [parentKeys, parentQueries]);

  // Distinguishes "this list genuinely has no entries" from "we have not loaded it, or the request
  // failed". Without it a network blip told the admin the client had no denial types, which is a
  // different problem with a different fix.
  const parentsResolved = parentQueries.every((query) => query.isSuccess);

  // Keyed once per load, not per render: withKeys mints a fresh id for an entry that has none, so
  // recomputing it on every render would remount every row and drop focus mid-edit.
  const baseItems = useMemo(() => withKeys(loaded?.items ?? []), [loaded]);
  // Null means untouched, mirroring `items`, so reopening a panel does not read as an edit.
  const alphabetize = alphabetized ?? lookup.alphabetize;
  const working = items ?? baseItems;
  // Either half of the panel can be the edit: the sort toggle changes what every picker shows even
  // when not a single entry was touched.
  const dirty = items !== null || alphabetized !== null;
  // Sorted for display only — `working` keeps the hand order underneath, so unticking the box
  // restores it without a save. Mirrors WorkflowLookup.ordered_items on the API.
  const ordered = alphabetize
    ? [...working].sort((a, b) => a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()))
    : working;
  const visible = showArchived ? ordered : ordered.filter((item) => !item.archived);
  const archivedCount = working.filter((item) => item.archived).length;
  // Counted off the boxes rather than the API's saved count, so it follows an edit or a Restore
  // before anything is saved. Always 0 for an option list, which declares no default wording.
  const customizedCount = working.filter((item) => !item.archived && isCustomized(item)).length;

  const update = useCallback((index: number, changes: Partial<WorkflowLookupItem>) => {
    setItems((current) => {
      const base = current ?? [];
      const next = [...base];
      const item = { ...next[index], ...changes };
      // The short name follows the name for as long as the entry is unsaved. Keying this off "the
      // short name is still empty" froze it at the first character typed, since one character is
      // enough to fill it.
      if (item._isNew) item.apiName = slugify(String(item.displayName ?? ''));
      next[index] = item;

      return next;
    });
  }, []);

  // Seeds the working copy from what loaded, so the first edit does not lose the rest of the list.
  const beginEditing = useCallback(() => {
    if (items === null && loaded?.items) setItems(baseItems);
  }, [items, loaded, baseItems]);

  const handleChange = useCallback(
    (index: number, changes: Partial<WorkflowLookupItem>) => {
      beginEditing();
      update(index, changes);
    },
    [beginEditing, update]
  );

  // Refuses to act until the list is in hand. Adding to a list that has not arrived yet would make
  // the new entry the WHOLE list, and saving that would archive every option the list really has.
  // Drops an entry outright rather than archiving it. Only ever called for one that has never been
  // saved, so nothing recorded points at it.
  const handleDiscard = useCallback((index: number) => {
    setItems((current) => (current ?? []).filter((_, position) => position !== index));
  }, []);

  const handleAdd = useCallback(() => {
    if (!loaded?.items) return;

    setItems([...(items ?? baseItems), blankItem(lookup.fields)]);
  }, [items, loaded, baseItems, lookup.fields]);

  const handleDragEnd = useCallback(
    (...[event]: Parameters<DragEndHandler>) => {
      if (event.canceled) return;
      const { source } = event.operation;
      if (!source || !isSortable(source)) return;
      if (source.initialIndex === source.index) return;

      if (!loaded?.items) return;

      setItems((current) => {
        const next = [...(current ?? baseItems)];
        const [moved] = next.splice(source.initialIndex, 1);
        next.splice(source.index, 0, moved);

        return next;
      });
    },
    [loaded, baseItems]
  );

  const save = useMutation({
    // Re-read before writing. The submitted array IS the list, so anything added by someone else
    // since this panel loaded would be archived by a save that never saw it — silently, with a 200.
    // Refusing and asking for a reload is the cheap version of a version check.
    mutationFn: async () => {
      const current = await showAdminWorkflowLookup(lookup.id);
      if (loaded && current.updatedAt !== loaded.updatedAt) throw new StaleListError();

      return updateAdminWorkflowLookup(lookup.id, toInput(working, lookup.fields), alphabetize);
    },
    // The saved row goes straight into the cache before the working copy is dropped. Invalidating
    // alone left the panel showing PRE-save data for the length of the refetch, and an edit started
    // in that window seeded itself from the stale copy and wrote the old values back on the next
    // save — a silent revert that answered 200.
    onSuccess: (saved) => {
      queryClient.setQueryData(adminWorkflowLookupsQueryKeys.show(lookup.id), saved);
      queryClient.invalidateQueries({ queryKey: adminWorkflowLookupsQueryKeys.index() });
      setItems(null);
      setAlphabetized(null);
      addToast({ text: 'List saved.' });
    },
    // The API's message names the entry and the problem ("other appears more than once"), which is
    // the only way to find the offender in a list of thirty. A generic failure hid all of that.
    onError: (error: unknown) => addToast({ text: saveErrorText(error) }),
  });

  // What this list needs said before anyone edits it. Collected rather than rendered one alert each
  // so they share a single box.
  //
  // The rename note is the non-obvious one: a referral records the short name and the words are
  // looked up when the page renders, so renaming reaches back into referrals already recorded. That
  // is right for a typo and wrong for repurposing, and nothing else on the screen says so.
  const notes = [
    lookup.family === OPTION_LIST && canEdit
      ? 'Renaming an option changes it everywhere, including on referrals already recorded. To give an option a ' +
        'different meaning, remove it and add a new one instead, so past referrals keep the old wording.'
      : null,
    lookup.family === DISPLAY_TEXT
      ? 'These names come from the workflow, so they can be renamed but not added to or removed. A name set here ' +
        'overrides the default, and can be restored.'
      : null,
    // Said before the save rather than after it: removing an entry another list still points at is
    // refused, and the reason is not visible from this panel on its own.
    lookup.usedBy.length > 0
      ? `${sentence(lookup.usedBy.map((list) => list.label))} entries are filed under this list, so an entry here ` +
        'cannot be removed while any of them still uses it.'
      : null,
    lookup.editable ? null : 'This list is no longer used by the workflow engine, so it is read only.',
  ].filter((note): note is string => note !== null);

  // Blocked before the request only to spare a round trip; the API enforces every one of these.
  const blocking = working.flatMap((item) => (item.archived ? [] : itemErrors(item, lookup.fields)));

  return (
    <Foldout open={open} position='right' onBackdropClick={animateClose} width='min(94%, 720px)'>
      <PanelContainer>
        <PanelHeader>
          <TitleGroup>
            <PanelTitle>{lookup.label}</PanelTitle>
            <Scope>
              {lookup.global ? 'Olio default' : `Customized for ${lookup.clientName}`} · {lookup.key}
            </Scope>
            {lookup.description && <Description>{lookup.description}</Description>}
            {customizedCount > 0 && (
              <Customized>
                <PencilIconOutline width={13} height={13} />
                {customizedCount} {customizedCount === 1 ? 'name' : 'names'} changed from the Olio default
              </Customized>
            )}
          </TitleGroup>
          <CloseButton onClick={animateClose} />
        </PanelHeader>

        <PanelContent>
          {/* One box, however many apply. A parent list that is also editable had three things to
              say and said them in three stacked alerts, which took more room than the list under
              them. */}
          {notes.length > 0 && (
            <Notice>
              {notes.map((note) => (
                <NoticeText key={note}>{note}</NoticeText>
              ))}
            </Notice>
          )}

          {isLoading ? (
            <SpinnerContainer>
              <CircularProgress color={colors.blue100} />
            </SpinnerContainer>
          ) : (
            <DragDropProvider onDragEnd={handleDragEnd}>
              {visible.map((item) => (
                <LookupItemRow
                  key={item._key}
                  item={item}
                  index={working.indexOf(item)}
                  fields={lookup.fields}
                  parentOptions={parentOptions}
                  parentsResolved={parentsResolved}
                  itemsEditable={lookup.itemsEditable}
                  alphabetized={alphabetize}
                  canEdit={canEdit}
                  onChange={(changes) => handleChange(working.indexOf(item), changes)}
                  onDiscard={() => handleDiscard(working.indexOf(item))}
                />
              ))}
            </DragDropProvider>
          )}
        </PanelContent>

        <PanelFooter>
          {/* Settings on the left, actions on the right. Sized to match the item rows' checkboxes;
              the shared default is 24px, which reads as a heading next to 14px labels. */}
          <FooterSettings>
            {lookup.itemsEditable && canEdit && (
              // Option lists only. Display text's order is the sequence the workflow declares its
              // steps in, so sorting it would turn a sequence into a word list.
              <Checkbox
                checked={alphabetize}
                label='Sort options alphabetically'
                size={16}
                labelSize='14px'
                onChange={(next: boolean) => setAlphabetized(next)}
              />
            )}

            {lookup.itemsEditable && archivedCount > 0 && (
              // Only offered where revealing them does something: an option list's archived entries
              // can be restored. A display-text entry is archived because the published workflow
              // stopped declaring it, so it can't be brought back — the only edit left is renaming a
              // label that now appears nowhere but old records, which is not a thing to invite.
              // They still ride along on save; this hides them, it doesn't drop them.
              <Checkbox
                checked={showArchived}
                label={`Show ${archivedCount} removed`}
                size={16}
                labelSize='14px'
                onChange={setShowArchived}
              />
            )}
          </FooterSettings>

          {lookup.itemsEditable && canEdit && (
            <Button variant='outline-primary' size='small' disabled={!loaded?.items} onClick={handleAdd}>
              Add option
            </Button>
          )}

          {canEdit && (
            <Button
              variant='primary'
              size='small'
              disabled={!dirty || !loaded?.items || blocking.length > 0 || save.isPending}
              loading={save.isPending}
              onClick={() => save.mutate()}>
              Save
            </Button>
          )}

          {blocking.length > 0 && <Blocking>Every option needs {sentence([...new Set(blocking)])}.</Blocking>}
        </PanelFooter>
      </PanelContainer>
    </Foldout>
  );
}

// Panel chrome copied from the sibling admin slideouts (HighCostMedListSlideout): a bordered
// header with an × close, a scrolling body, and the actions pinned in a bordered footer.
const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 32px 20px;
  border-bottom: 1px solid ${colors.black10};
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PanelTitle = styled.h2`
  font-size: ${({ theme }) => theme.textStyles.h3.fontSize};
  line-height: ${({ theme }) => theme.textStyles.h3.lineHeight};
  font-weight: ${({ theme }) => theme.textStyles.h3.fontWeight};
  margin: 0;
`;

const PanelContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px 32px;
`;

const FooterSettings = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-right: auto;
`;

const PanelFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  border-top: 1px solid ${colors.black10};
`;

const Scope = styled(BodySmall)`
  color: ${colors.black50};
`;

const Customized = styled(BodySmall)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  color: ${colors.black50};
`;

const Description = styled(BodySmall)`
  display: block;
  margin-top: 4px;
  color: ${colors.black75};
`;

const Notice = styled(Alert)`
  margin-bottom: 16px;
  /* Alert centers its icon, which is right for the one-line alerts it is used for elsewhere and
     wrong here: this box runs to several lines, and a centered icon floats away from the text it
     belongs to. Overridden here rather than in the shared component so the other seven usages keep
     the centering that suits them. */
  align-items: flex-start;

  /* Then nudged back down onto the first line rather than flush with the box: the text is 20px
     line-height over a 16px icon, so 2px centres it against the words beside it. Reaches Alert's
     own icon wrapper, which it does not expose. */
  > div:first-child {
    padding-top: 2px;
  }
`;

const NoticeText = styled(Alert.Text)`
  margin: 0;

  & + & {
    margin-top: 8px;
  }
`;

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
`;

const Blocking = styled(BodySmall)`
  display: block;
  margin-top: 8px;
  color: ${colors.red100};
`;
